import { FC, useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import classNames from "classnames";
import styles from './../../styles/index.module.scss'
import { DocumentType, IdTableVariant } from "../../../@types/enumsGlobal"
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import useGridFilter, { MarketingCompensatedDiscountsDefaultGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import marketingStyles from './styles/MarketingCompensatedDiscounts.module.scss'
import tabsStyles from '../../../pages/styles/homePage.module.scss'
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import TabsPanel from "../../../components/tabs/TabsPanel";
import { usePluginContext } from "../../../system/providers/plugin/PluginContextProvider";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DateTime } from "luxon";
import { t } from "i18next";
import { Accordion } from "../../../components/controls/accordion";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import FlexRow from "../../../components/controls/FlexRow";
import { DatePeriodPanelV2 } from "../../../hoc/DatePeriodPanelV2/DatePeriodPanelV2";
import InputSearch from "../../../components/inputSearch/inputSearch";
import React from "react";
import MarketingCompensatedDiscountCreatorModal from "./MarketingCompensatedDiscountsCreatorModal";
import { MarketingCompensatedDiscountsDataProvider } from "../../../Services/DataProviders/MarketingCompensatedDiscountsDataProvider";
import { IMarketingCompensatedDiscountsViewDTO } from "../../../libs/coreapi-dto/dirs/marketingCompensatedDiscounts";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { ContractorDataProvider } from "../../../Services/DataProviders/ContractorDataProvider";
import EntityMultiSelect from "../../../components/entityMultiSelect/entityMultiSelect";
import { treeViewFolderPharmacy } from "../../../constants";
import { ISelectedItems } from "./interfaces";

export const MarketingCompensatedDiscountsPluginView: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const pluginContext = usePluginContext();
    const tabCtx = useTabsContext();
    const userContext = useUserContext();

    const marketingCompensatedDiscountsDP = new MarketingCompensatedDiscountsDataProvider(appCtx.coreApiService);
    const contractorDP = new ContractorDataProvider(appCtx.coreApiService)

    const [data, setData] = useState<IMarketingCompensatedDiscountsViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [viewState, setViewState] = useState<GridStateType>("view");


    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -7 }), endDate: DateTime.now() } as IDatePeriod;
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [gridFilter, dispatchGridFilter] = useGridFilter(MarketingCompensatedDiscountsDefaultGridFilter(datePeriod));
    const [gridFilterPharmacies] = useGridFilter({
        numberPerPage: 1000,
        pageNumber: 1,
        columnFilters: [],
    });

    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);

    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [isPharmaciesLoading, setIsPharmaciesLoading] = useState<boolean>(true)
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);

    const baseT = (value: string) => t('directory.marketing.' + value);

    const [currentCompensatedDiscountValue, setCurrentCompensatedDiscountValue] = useState<string>('')
    const [pharmacy, setPharmacy] = useState<IEntitySimpleDTO>();
    const [currentContractorValue, setCurrentContractorValue] = useState<string>('')
    const [currentGoodsValue, setCurrentGoodsValue] = useState<string>('')
    const [pharmacies, setPharmacies] = useState<IEntitySimpleDTO[]>([]);
    const [selectedPharmacies, setSelectedPharmacies] = useState<ISelectedItems>();

    const marketingFilters = {
        goodsNameFilter: currentGoodsValue,
        contractorNameFilter: currentContractorValue,
        pharmaciesFilter: selectedPharmacies,
    }

    const handleDateFilterChange = useCallback((date: IDatePeriod) => {

        const filtersWithoutDates = gridFilter.columnFilters.filter(
            filter => filter.name !== "dateFrom" && filter.name !== "dateTo"
        );

        const newFilters: IGridColumnFilter[] = [...filtersWithoutDates];
        if (date?.endDate) {
            newFilters.push({
                name: "dateFrom",
                value: date.endDate.toFormat("yyyyMMdd"),
                operator: "LessOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        if (date?.startDate) {
            newFilters.push({
                name: "dateTo",
                value: date.startDate.toFormat("yyyyMMdd"),
                operator: "MoreOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        dispatchGridFilter({
            type: "newColumnFilters",
            payload: newFilters
        });
    }, [dispatchGridFilter, gridFilter.columnFilters]);

    const handleDatePeriodChange = useCallback((date: IDatePeriod) => {
        setDatePeriod(date);
        handleDateFilterChange(date);
    }, [handleDateFilterChange]);

    const handleSearchCompensatedDiscountChange = (value: string) => {
        setCurrentCompensatedDiscountValue(value);
    };

    const handleSearchContractorChange = (value: string) => {
        setCurrentContractorValue(value);
    };

    const handleSearchGoodsChange = (value: string) => {
        setCurrentGoodsValue(value);
    };

    const handleSearchPharmaciesIdsChange = (selectedItems: ISelectedItems) => {
        setSelectedPharmacies(selectedItems);
        };

    const handlePharmacyFilterChange = (pharmacy?: IEntitySimpleDTO) => {
        const normalizedValue = pharmacy?.idGlobal === undefined ? null : pharmacy.idGlobal;

        dispatchGridFilter({
            type: "deleteColumnFilter",
            payload: "contractor"
        });

        if (normalizedValue !== null && normalizedValue !== '') {
            dispatchGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: "contractor",
                    value: normalizedValue,
                    operator: 'Eq',
                    invisible: false
                } as IGridColumnFilter
            });
        }
    };

    function getPharmacies() {
        const userContractorsFilter = {
            ...gridFilterPharmacies,
            paramFilter: {
                idGroupGlobal: treeViewFolderPharmacy.folderId,
                idUserGlobal: userContext.idGlobal
            }
        } as IGridFilter;
        setIsPharmaciesLoading(true)
        contractorDP.getView(userContractorsFilter, (values) => {
            setPharmacies(values)
            setIsPharmaciesLoading(false)
        })
    }

    useEffect(() => {
        handlePharmacyFilterChange(pharmacy)
    }, [pharmacy])

    useEffect(() => {
        getPharmacies();
    }, [])

    useEffect(() => {
        setIsSubmitting(true)

        marketingCompensatedDiscountsDP.getViewParam(gridFilter, async (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            setLoadingStatus(LoadingStatus.Completed);
            setIsSubmitting(false);
            setSelectedItem(undefined)

        })
    }, [gridFilter])

    useEffect(() => {
        pluginContext.masterGrid.onUnselectEvent();
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (viewState === 'refresh') {
            setIsSubmitting(true);

            marketingCompensatedDiscountsDP.getViewParam(gridFilter, async (data, totalCount) => {
                setData(data);
                setViewState('view');
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
                setIsSubmitting(false);
            })
        }
    }, [viewState])

    return (
        <PluginWrapper>
            <ToolbarWrapper className={marketingStyles.toolbarWrapper}>
                <DefaultDictionaryCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    dataProvider={marketingCompensatedDiscountsDP}
                    creatorModalJsx={MarketingCompensatedDiscountCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    multipleSelect={[multipleSelect, setMultipleSelect]}
                />
                <DatePeriodPanelV2
                    datePeriod={datePeriod}
                    onDatePeriodChange={handleDatePeriodChange}
                />
            </ToolbarWrapper>
            <Accordion
                opened={false}
                caption={baseT("filters")}
                id={`${uuidv4()}`}
            >
                <FlexRow>
                    <div className={marketingStyles.filtersWrapper}>
                        <InputSearch
                            searchValue={currentCompensatedDiscountValue}
                            typeFilter={'columnFilter'}
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchCompensatedDiscountChange}
                            filterName="name"
                            operator="Like"
                            placeholder={baseT("name")}
                            debounceDelay={1000}
                        />
                        <div className={marketingStyles.filterContainer}>
                            <EntityMultiSelect
                                loading={isPharmaciesLoading}
                                items={pharmacies}
                                filterName="IdContractorGlobalList"
                                gridFilter={gridFilter}
                                dispatchGridFilter={dispatchGridFilter}
                                handleSearchChange={handleSearchPharmaciesIdsChange}
                                placeholder={baseT("pharmacy")}
                                showClearButton={true}
                            />
                        </div>
                    </div>
                </FlexRow>

                <FlexRow>
                    <div className={marketingStyles.filtersWrapper}>
                        <InputSearch
                            searchValue={currentGoodsValue}
                            typeFilter={'paramFilter'}
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchGoodsChange}
                            filterName="goodsName"
                            placeholder={baseT("goods")}
                            debounceDelay={1000}
                        />
                        <InputSearch
                            searchValue={currentContractorValue}
                            typeFilter={'paramFilter'}
                            gridFilter={gridFilter}
                            dispatchGridFilter={dispatchGridFilter}
                            handleSearchChange={handleSearchContractorChange}
                            filterName="legalEntityName"
                            placeholder={baseT("contractors")}
                            debounceDelay={1000}
                        />
                    </div>
                </FlexRow>
            </Accordion>
            {
                isSubmitting ? <div className={marketingStyles.loading}><Spinner /></div>
                    :
                    <div className={styles.gridWrapper}>
                        <div className={styles.masterGrid}>
                            <DefaultGrid
                                searching
                                isServerFilter
                                showQuantity
                                gridId={props.gridId}
                                data={data}
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                separator
                                singleDirectory
                                totalCount={totalCount}
                                filter={gridFilter}
                                dataProvider={marketingCompensatedDiscountsDP}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                multipleSelect={multipleSelect}
                                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                getView={(gridFilter, callback) => {
                                    marketingCompensatedDiscountsDP.getViewParam(gridFilter, (entities) => {
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row)
                                    props.onSelect?.(row)
                                    row
                                        ? pluginContext.masterGrid.onSelectEvent(row, DocumentType.marketingEvents)
                                        : pluginContext.masterGrid.onUnselectEvent()
                                }}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows)
                                    props?.onMultipleSelect?.(rows)
                                }}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                onEnter={() => setViewState("edit")}
                            />
                        </div>
                        <div className={styles.detailsTabView}>
                            {
                                detailsTabsPanel.tabs.length > 0 &&
                                selectedItem &&
                                !multipleSelect &&
                                (
                                    <div>
                                        <div className={tabsStyles.tabsPanelWrapper}>
                                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: 'hidden', width: '100%' }}>
                                                <TabsPanel
                                                    id="detailsTabsPanel"
                                                    activeId={detailsTabsPanel.currentTab?.id}
                                                    tabs={detailsTabsPanel.tabs}
                                                    onActive={(tab) => {
                                                        dispatchDetailsTabsPanel({ type: "activate", payload: tab })
                                                    }}>
                                                </TabsPanel>
                                            </div>
                                        </div>
                                        <div className={tabsStyles.contentWrapper}>
                                            {detailsTabsPanel.tabs.map(item => {
                                                const isActive = item.id === detailsTabsPanel.currentTab?.id;
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className={classNames(isActive ? tabsStyles.contentVisible : tabsStyles.contentHidden)}
                                                    >
                                                        {isActive && React.isValidElement(item.view.content) ? (
                                                            React.cloneElement(item.view.content, {
                                                                key: `${item.id}_${currentGoodsValue}}_${selectedPharmacies}`,
                                                                marketingFilters: marketingFilters
                                                            } as any)
                                                        ) : (
                                                            item.view.content
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
            }
        </PluginWrapper>
    )
}